import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, admin } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import AdminHeader from '../components/AdminHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Mail,
  Inbox,
  Send,
  Trash2,
  Search,
  RefreshCw,
  Settings,
  Plus,
  ChevronLeft,
  Paperclip,
  Folder,
  FolderPlus,
  Check,
  AlertCircle
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminEmails() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Email state
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [unreadCounts, setUnreadCounts] = useState({
    inbox: 0,
    'ai-processed': 0,
    sent: 0,
    trash: 0,
    spam: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const [composerMode, setComposerMode] = useState('new'); // 'new', 'reply', 'forward'
  const [composerData, setComposerData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [activityLogs, setActivityLogs] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [emailThread, setEmailThread] = useState([]);

  // Folder management state
  const [customFolders, setCustomFolders] = useState([]);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Delete confirmation modal state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'single' | 'bulk', ids: [] }

  // Drag and drop state
  const [draggedEmails, setDraggedEmails] = useState(null);

  // Define handleCompose before useEffect that uses it
  const handleCompose = useCallback(() => {
    setComposerMode('new');
    setComposerData({ to: '', subject: '', body: '' });
    setShowComposer(true);
  }, []);

  useEffect(() => {
    // Check if user is authenticated and is admin
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const userData = auth.getUser();
    if (!userData?.is_admin) {
      navigate('/dashboard');
      return;
    }

    setUser(userData);
    loadEmails();
    loadUnreadCounts();
  }, [navigate, selectedFolder]);

  // Handle compose button click from AdminHeader
  useEffect(() => {
    const composeBtn = document.getElementById('compose-btn');
    if (composeBtn) {
      composeBtn.addEventListener('click', handleCompose);
      return () => composeBtn.removeEventListener('click', handleCompose);
    }
  }, [handleCompose]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const response = await admin.listEmails({
        folder: selectedFolder,
        search: searchQuery || undefined,
        limit: 50,
        skip: 0
      });
      setEmails(response.data?.emails || []);
      setError('');
    } catch (err) {
      console.error('Error loading emails:', err);
      setError('Failed to load emails');
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await admin.getUnreadCounts();
      setUnreadCounts(response.data || {
        inbox: 0,
        'ai-processed': 0,
        sent: 0,
        trash: 0
      });
    } catch (err) {
      console.error('Error loading unread counts:', err);
      setUnreadCounts({
        inbox: 0,
        'ai-processed': 0,
        sent: 0,
        trash: 0
      });
    }
  };

  const handleEmailClick = async (email) => {
    try {
      const response = await admin.getEmail(email.id);
      setSelectedEmail(response.data);

      // Load activity logs
      const activityResponse = await admin.getEmailActivity(email.id);
      setActivityLogs(activityResponse.data?.logs || []);

      // Load email thread
      const threadResponse = await admin.getEmailThread(email.id);
      setEmailThread(threadResponse.data || []);
    } catch (err) {
      console.error('Error loading email details:', err);
      setError('Failed to load email details');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    setDeleteTarget({ type: 'single', ids: [emailId] });
    setShowDeleteDialog(true);
  };

  const handleBulkDelete = () => {
    if (selectedEmails.size === 0) return;
    setDeleteTarget({ type: 'bulk', ids: Array.from(selectedEmails) });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      for (const emailId of deleteTarget.ids) {
        await admin.deleteEmail(emailId, false);
      }
      loadEmails();
      loadUnreadCounts();
      setSelectedEmail(null);
      setSelectedEmails(new Set());
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting email(s):', err);
      setError('Failed to delete email(s)');
    }
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedEmails.size === emails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(emails.map(e => e.id)));
    }
  };

  const handleReply = () => {
    if (!selectedEmail) return;
    setComposerMode('reply');
    setComposerData({
      to: selectedEmail.from?.email || '',
      subject: `Re: ${selectedEmail.subject}`,
      body: `\n\n---\nOn ${formatDate(selectedEmail.timestamp)}, ${selectedEmail.from?.name || selectedEmail.from?.email} wrote:\n${selectedEmail.body_text || ''}`
    });
    setShowComposer(true);
  };

  const handleForward = () => {
    if (!selectedEmail) return;
    setComposerMode('forward');
    setComposerData({
      to: '',
      subject: `Fwd: ${selectedEmail.subject}`,
      body: `\n\n---\nForwarded message from ${selectedEmail.from?.name || selectedEmail.from?.email}:\n${selectedEmail.body_text || ''}`
    });
    setShowComposer(true);
  };

  const handleSendEmail = async () => {
    try {
      await admin.sendEmail({
        to: composerData.to,
        subject: composerData.subject,
        text: composerData.body,
        html: composerData.body.replace(/\n/g, '<br>'),
        thread_id: composerMode === 'reply' ? selectedEmail?.thread_id : undefined
      });
      setShowComposer(false);
      setComposerData({ to: '', subject: '', body: '' });
      loadEmails();
      setError('');
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Failed to send email');
    }
  };

  const handleMoveToFolder = async (emailId, folder) => {
    try {
      await admin.updateEmail(emailId, { folder });
      loadEmails();
      loadUnreadCounts();
      setSelectedEmail(null);
    } catch (err) {
      console.error('Error moving email:', err);
      setError('Failed to move email');
    }
  };

  const handleBulkMoveToFolder = async (folder) => {
    if (selectedEmails.size === 0) return;

    try {
      for (const emailId of selectedEmails) {
        await admin.updateEmail(emailId, { folder });
      }
      loadEmails();
      loadUnreadCounts();
      setSelectedEmails(new Set());
    } catch (err) {
      console.error('Error moving emails:', err);
      setError('Failed to move emails');
    }
  };

  const handleDragStart = (e, emailIds) => {
    setDraggedEmails(emailIds);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetFolder) => {
    e.preventDefault();
    if (!draggedEmails || draggedEmails.length === 0) return;

    try {
      for (const emailId of draggedEmails) {
        await admin.updateEmail(emailId, { folder: targetFolder });
      }
      loadEmails();
      loadUnreadCounts();
      setDraggedEmails(null);
      setSelectedEmails(new Set());
    } catch (err) {
      console.error('Error moving emails:', err);
      setError('Failed to move emails');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    // For now, store custom folders in localStorage
    // In a real implementation, this would be an API call
    const updatedFolders = [...customFolders, {
      id: newFolderName.toLowerCase().replace(/\s+/g, '-'),
      label: newFolderName,
      icon: Folder
    }];
    setCustomFolders(updatedFolders);

    // Only save id and label to localStorage (icon cannot be serialized)
    const foldersToSave = updatedFolders.map(({ id, label }) => ({ id, label }));
    localStorage.setItem('customEmailFolders', JSON.stringify(foldersToSave));

    setNewFolderName('');
    setShowNewFolderDialog(false);
  };

  // Load custom folders from localStorage on mount
  useEffect(() => {
    const savedFolders = localStorage.getItem('customEmailFolders');
    if (savedFolders) {
      try {
        const parsed = JSON.parse(savedFolders);
        // Restore the icon component reference when loading from localStorage
        const foldersWithIcons = parsed.map(folder => ({
          ...folder,
          icon: Folder
        }));
        setCustomFolders(foldersWithIcons);
      } catch (e) {
        console.error('Error loading custom folders:', e);
      }
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const defaultFolders = [
    { id: 'inbox', label: 'Inbox', icon: Inbox, isDefault: true },
    { id: 'ai-processed', label: 'AI Processed', icon: Mail, isDefault: true },
    { id: 'sent', label: 'Sent', icon: Send, isDefault: true },
    { id: 'spam', label: 'Spam', icon: AlertCircle, isDefault: true },
    { id: 'trash', label: 'Trash', icon: Trash2, isDefault: true }
  ];

  const folders = [...defaultFolders, ...customFolders];

  if (!user) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Bulk Actions Bar - Shows when emails are selected */}
      {selectedEmails.size > 0 && (
        <div className="flex-none bg-purple-900/30 border-b border-purple-500/20 backdrop-blur-xl w-full">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">
                {selectedEmails.size} email{selectedEmails.size !== 1 ? 's' : ''} selected
              </span>
              <Button
                variant="outline"
                onClick={handleBulkDelete}
                className="border-red-500/20 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Fills remaining height */}
      <div className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-4 flex flex-col min-h-0">
        {error && (
          <Alert variant="destructive" className="mb-4 flex-none">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
          {/* Left Sidebar - Folders */}
          <div className="col-span-12 md:col-span-3 min-h-0">
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl flex flex-col h-full">
              <CardHeader className="pb-3 flex-none">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base">Folders</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewFolderDialog(true)}
                    className="h-7 w-7 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-1 pt-0 flex-1 overflow-y-auto min-h-0">
                {folders.map((folder) => {
                  const Icon = folder.icon;
                  const count = unreadCounts[folder.id] || 0;
                  return (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, folder.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                        selectedFolder === folder.id
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4" />
                        <span className="text-sm">{folder.label}</span>
                      </div>
                      {count > 0 && (
                        <Badge variant="secondary" className="bg-purple-500 text-white text-xs">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Center - Email List */}
          <div className="col-span-12 md:col-span-5 min-h-0">
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl flex flex-col h-full">
              <CardHeader className="pb-3 flex-none">
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search emails..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && loadEmails()}
                      className="pl-10 bg-gray-800 border-gray-700 text-white text-sm h-9"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={loadEmails}
                    className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10 h-9 w-9"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {emails.length > 0 && (
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={selectedEmails.size === emails.length && emails.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-gray-400">
                      {selectedEmails.size > 0 ? `${selectedEmails.size} selected` : 'Select all'}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0 pt-0 flex-1 overflow-y-auto min-h-0">
                <div className="divide-y divide-gray-800">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="p-3">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    ))
                  ) : emails.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      No emails found
                    </div>
                  ) : (
                    emails.map((email) => (
                      <div
                        key={email.id}
                        draggable={selectedEmails.has(email.id)}
                        onDragStart={(e) => handleDragStart(e, selectedEmails.has(email.id) ? Array.from(selectedEmails) : [email.id])}
                        className={`flex items-start space-x-2 p-3 hover:bg-gray-800 transition-colors ${
                          selectedEmail?.id === email.id ? 'bg-gray-800' : ''
                        } ${email.status === 'unread' ? 'font-semibold' : ''} ${
                          selectedEmails.has(email.id) ? 'bg-purple-900/20' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(email.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleEmailSelection(email.id);
                          }}
                          className="mt-1 h-4 w-4 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-500"
                        />
                        <button
                          onClick={() => handleEmailClick(email)}
                          className="flex-1 text-left"
                        >
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-white truncate flex-1 text-sm">
                              {email.from?.name || email.from?.email || 'Unknown'}
                            </span>
                            <span className="text-xs text-gray-400 ml-2">
                              {formatDate(email.timestamp)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-300 truncate mb-1">
                            {email.subject}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-400 truncate flex-1">
                              {email.body_preview}
                            </div>
                            {email.has_attachments && (
                              <Paperclip className="h-3 w-3 text-gray-400 ml-2" />
                            )}
                          </div>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right - Email Detail */}
          <div className="col-span-12 md:col-span-4 min-h-0">
            {selectedEmail ? (
              <Card className="bg-white border-gray-200 flex flex-col h-full">
                <CardHeader className="pb-3 bg-gray-50 border-b border-gray-200 flex-none">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-2">
                        {selectedEmail.subject}
                      </h3>
                      <div className="text-xs text-gray-600">
                        <div>From: {selectedEmail.from?.name || selectedEmail.from?.email}</div>
                        <div>To: {selectedEmail.to?.map(t => t.email || t).join(', ')}</div>
                        <div>{formatDate(selectedEmail.timestamp)}</div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-3 flex-1 overflow-y-auto min-h-0">
                  <div className="mb-3 flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReply}
                      className="border-purple-300 text-purple-600 hover:bg-purple-50 h-8 text-xs"
                    >
                      Reply
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleForward}
                      className="border-purple-300 text-purple-600 hover:bg-purple-50 h-8 text-xs"
                    >
                      Forward
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteEmail(selectedEmail.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Email Thread View */}
                  {emailThread.length > 1 ? (
                    <div className="space-y-3">
                      {emailThread.map((threadEmail, index) => (
                        <div key={threadEmail.id} className={`border rounded-lg p-3 ${
                          threadEmail.id === selectedEmail.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="text-xs font-medium text-gray-900">
                              {threadEmail.from?.name || threadEmail.from?.email}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(threadEmail.timestamp)}
                            </div>
                          </div>
                          <div
                            className="prose prose-sm max-w-none text-gray-800"
                            dangerouslySetInnerHTML={{ __html: threadEmail.body_html || threadEmail.body_text?.replace(/\n/g, '<br>') }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="prose prose-sm max-w-none text-gray-800"
                      dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || selectedEmail.body_text?.replace(/\n/g, '<br>') }}
                    />
                  )}

                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Attachments</h4>
                      <div className="space-y-2">
                        {selectedEmail.attachments.map((attachment, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-2 bg-gray-100 rounded"
                          >
                            <Paperclip className="h-4 w-4 text-gray-600" />
                            <span className="text-sm text-gray-700">{attachment.filename}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Activity Log */}
                  {activityLogs.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => setShowActivity(!showActivity)}
                        className="flex items-center justify-between w-full text-sm font-semibold text-gray-900 mb-2 hover:text-purple-600"
                      >
                        <span>Activity Log ({activityLogs.length})</span>
                        <span>{showActivity ? '▼' : '▶'}</span>
                      </button>
                      {showActivity && (
                        <div className="space-y-2 mt-2">
                          {activityLogs.map((log, index) => (
                            <div key={log.id || index} className="p-2 bg-gray-100 rounded text-sm">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-purple-600 font-medium text-xs">
                                  {log.activity_type.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                <span className="text-gray-500 text-xs">
                                  {new Date(log.timestamp).toLocaleString()}
                                </span>
                              </div>
                              {log.metadata && (
                                <div className="text-gray-600 text-xs">
                                  {log.metadata.message && <div>{log.metadata.message}</div>}
                                  {log.metadata.rule_name && <div>Rule: {log.metadata.rule_name}</div>}
                                  {log.metadata.actions && (
                                    <div>
                                      {log.metadata.actions.folder_moved && <div>→ Moved to: {log.metadata.actions.folder_moved}</div>}
                                      {log.metadata.actions.response_sent && <div>→ Response sent</div>}
                                      {log.metadata.actions.admin_notified && <div>→ Admin notified</div>}
                                    </div>
                                  )}
                                  {log.metadata.error && <div className="text-red-600">Error: {log.metadata.error}</div>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-gray-200 flex flex-col h-full">
                <CardContent className="flex items-center justify-center flex-1">
                  <div className="text-center text-gray-400">
                    <Mail className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Select an email to view</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Email Composer Modal */}
      {showComposer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-gray-900 border-purple-500/20 w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle className="text-white">
                {composerMode === 'new' && 'Compose Email'}
                {composerMode === 'reply' && 'Reply'}
                {composerMode === 'forward' && 'Forward'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">To</label>
                <Input
                  value={composerData.to}
                  onChange={(e) => setComposerData({ ...composerData, to: e.target.value })}
                  placeholder="recipient@example.com"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                <Input
                  value={composerData.subject}
                  onChange={(e) => setComposerData({ ...composerData, subject: e.target.value })}
                  placeholder="Email subject"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Message</label>
                <textarea
                  value={composerData.body}
                  onChange={(e) => setComposerData({ ...composerData, body: e.target.value })}
                  placeholder="Type your message..."
                  rows={10}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowComposer(false)}
                  className="border-gray-700 text-gray-400 hover:bg-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendEmail}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-gray-900 border-purple-500/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Delete</DialogTitle>
            <DialogDescription className="text-gray-400">
              {deleteTarget?.type === 'bulk'
                ? `Are you sure you want to move ${deleteTarget.ids.length} email(s) to trash?`
                : 'Are you sure you want to move this email to trash?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteTarget(null);
              }}
              className="border-gray-700 text-gray-400 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent className="bg-gray-900 border-purple-500/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Folder</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter a name for your new email folder.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="bg-gray-800 border-gray-700 text-white"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewFolderDialog(false);
                setNewFolderName('');
              }}
              className="border-gray-700 text-gray-400 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

